import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { ReactComponent as LogoutIcon } from '../../assets/icons/logout.svg';
import { ReactComponent as PowerByEnergic } from '../../assets/icons/sidebar/powered-by-energic.svg';
import { ReactComponent as AccountActiveIcon } from '../../assets/icons/sidebar/sidebar-account-active.svg';
import { ReactComponent as AccountIcon } from '../../assets/icons/sidebar/sidebar-account.svg';
import { ReactComponent as HomeActiveIcon } from '../../assets/icons/sidebar/sidebar-home-active.svg';
import { ReactComponent as HomeIcon } from '../../assets/icons/sidebar/sidebar-home.svg';
import { ReactComponent as QRCodeActiveIcon } from '../../assets/icons/sidebar/sidebar-qrcode-active.svg';
import { ReactComponent as QRCodeIcon } from '../../assets/icons/sidebar/sidebar-qrcode.svg';
import { ReactComponent as ReportActiveIcon } from '../../assets/icons/sidebar/sidebar-report-active.svg';
import { ReactComponent as ReportIcon } from '../../assets/icons/sidebar/sidebar-report.svg';
import { ReactComponent as RevenueActiveIcon } from '../../assets/icons/sidebar/sidebar-revenue-active.svg';
import { ReactComponent as RevenueIcon } from '../../assets/icons/sidebar/sidebar-revenue.svg';
import { ReactComponent as StationActiveIcon } from '../../assets/icons/sidebar/sidebar-station-active.svg';
import { ReactComponent as StationIcon } from '../../assets/icons/sidebar/sidebar-station.svg';
import { ReactComponent as UserActiveIcon } from '../../assets/icons/sidebar/sidebar-user-active.svg';
import { ReactComponent as UserIcon } from '../../assets/icons/sidebar/sidebar-user.svg';
import { useAppBranding } from '../../config/branding';
// import config from '../../config/config';
import { useBranding } from '../../contexts/BrandingContext';
import LanguageSelector from '../../contexts/language-selector/LanguageSelector';
import { useAppDispatch } from '../../redux/hooks';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import { getCookie, removeCookie } from '../../shared-functions/Cookies';
import getFirstSubdomain from '../../shared-functions/DomainsplitFunction';
import { permissionCheck } from '../../shared-functions/JWTDocodeFunction';
import authApi from '../../utils/api/auth';

/**
 * ChevronDown component
 * @returns ChevronDown component
 */
function ChevronDown() {
  return (
    <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M1 1L5 5L9 1"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * ChevronRight component
 * @returns ChevronRight component
 */
function ChevronRight() {
  return (
    <svg width="6" height="10" viewBox="0 0 6 10" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M1 9L5 5L1 1"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

interface MenuItem {
  id: string;
  label: string;
  url?: string;
  svgIcon?: React.ReactNode;
  activeSvgIcon?: React.ReactNode;
  allow: boolean;
  children?: MenuItem[];
  isOpen?: boolean;
  activeMatch?: string;
}

/**
 * Sidebar component
 * @returns Sidebar component
 */
function Sidebar() {
  const { t, i18n } = useTranslation('sharedComponent');
  const branding = useAppBranding();
  const sidebarReference = useRef<HTMLDivElement>(null);
  const [expandSidebar, setExpandSidebar] = useState<boolean>(true);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { images: brandImage } = useAppBranding();
  const { brandData } = useBranding();
  const subdomain = getFirstSubdomain();
  const shouldHideAccessUtility = Boolean(subdomain);
  const cachedLocal = JSON.parse(localStorage.getItem('app_branding_config') || ('{}' as any));
  const [menuItems, setMenuItems] = useState<MenuItem[]>([
    {
      id: 'dashboard',
      label: 'dashboard',
      url: '/ev-admin/dashboard',
      svgIcon: <HomeIcon />,
      activeSvgIcon: <HomeActiveIcon />,
      allow: permissionCheck('dashboard'),
    },
    {
      id: 'accounts',
      label: 'accounts',
      svgIcon: <AccountIcon />,
      activeSvgIcon: <AccountActiveIcon />,
      allow: true,
      children: [
        {
          id: 'partners',
          label: 'partners',
          url: '/ev-admin/account',
          allow: permissionCheck('accounts_management'),
        },
        {
          id: 'utilityProvider',
          label: 'utilityProvider',
          url: '/ev-admin/utility-provider',
          allow: permissionCheck('accounts_management'),
        },
        {
          id: 'endConsumer',
          label: 'endConsumer',
          url: '/ev-admin/end-consumer',
          // allow: permissionCheck('end_consumer_management'),
          allow:
            permissionCheck('end_consumer_management') && (!brandData?.useSharedDatabase as any),
        },
      ],
    },
    {
      id: 'operations',
      label: 'operations',
      svgIcon: <StationIcon />,
      activeSvgIcon: <StationActiveIcon />,
      allow: true,
      children: [
        {
          id: 'chargingStations',
          label: 'chargingStation',
          url: '/ev-admin/charging-station/approved',
          allow: permissionCheck('charging_station'),
          activeMatch: '/ev-admin/charging-station',
        },
        {
          id: 'chargers',
          label: 'chargers',
          url: '/ev-admin/device-management',
          allow: permissionCheck('device_management') || permissionCheck('view_device'),
        },
        {
          id: 'sessions',
          label: 'sessions',
          url: '/ev-admin/charging-sessions',
          allow: permissionCheck('payments'),
        },
      ],
    },
    {
      id: 'finance',
      label: 'finance',
      svgIcon: <RevenueIcon />,
      activeSvgIcon: <RevenueActiveIcon />,
      allow: true,
      children: [
        {
          id: 'revenue',
          label: 'revenue',
          allow: true,
          children: [
            {
              id: 'transaction',
              label: 'transactions',
              url: '/ev-admin/payments',
              allow: permissionCheck('payments'),
            },
            {
              id: 'revenueSharing',
              label: 'revenueSharing',
              url: '/ev-admin/revenue/sharing',
              allow: permissionCheck('wallet_revenue_sharing'),
            },
            {
              id: 'payouts',
              label: 'payouts',
              url: '/ev-admin/stripe-payouts/approved',
              allow: permissionCheck('stripe_payout_management') && !shouldHideAccessUtility,
              activeMatch: '/ev-admin/stripe-payouts',
            },
          ],
        },
        {
          id: 'configuration',
          label: 'configuration',
          allow: true,
          children: [
            {
              id: 'dynamicPricing',
              label: 'dynamicPricingApproval',
              url: '/ev-admin/dynamic-pricing/approved',
              allow: permissionCheck('dynamic_pricing'),
            },
            {
              id: 'revenueSharingManagement',
              label: 'revenueManagement',
              url: '/ev-admin/revenue/management',
              allow: permissionCheck('wallet_revenue_sharing'),
            },
            {
              id: 'feeConfiguration',
              label: 'feeConfiguration',
              url: '/ev-admin/fee-configuration',
              allow: true,
            },
            {
              id: 'idleFees',
              label: 'idleFee',
              url: '/ev-admin/penalty-management/overrides',
              allow: true,
            },
          ],
        },
      ],
    },
    {
      id: 'reports',
      label: 'reports',
      url: '/ev-admin/reports/energyReport',
      svgIcon: <ReportIcon />,
      activeSvgIcon: <ReportActiveIcon />,
      allow: permissionCheck('reporting_tools'),
      children: [
        {
          id: 'energyReport',
          label: 'energyReport',
          url: '/ev-admin/reports/energyReport',
          allow: permissionCheck('reporting_tools'),
        },
        {
          id: 'revenueReport',
          label: 'revenueReport',
          url: '/ev-admin/reports/revenueReport',
          allow: permissionCheck('reporting_tools'),
        },
        {
          id: 'bookingsReport',
          label: 'bookingsReport',
          url: '/ev-admin/reports/bookingsReport',
          allow: permissionCheck('reporting_tools'),
        },
        {
          id: 'chargerUtilizationReport',
          label: 'chargerUtilizationReport',
          url: '/ev-admin/reports/chargerUtilizationReport',
          allow: permissionCheck('reporting_tools'),
        },
        {
          id: 'performanceReport',
          label: 'performanceReport',
          url: '/ev-admin/reports/performanceReport',
          allow: permissionCheck('reporting_tools'),
        },
        {
          id: 'maintenanceReport',
          label: 'maintenanceReport',
          url: '/ev-admin/reports/maintenanceReport',
          allow: permissionCheck('reporting_tools'),
        },
      ],
    },
    {
      id: 'settings',
      label: 'settings',
      svgIcon: <UserIcon />,
      activeSvgIcon: <UserActiveIcon />,
      allow: true,
      children: [
        {
          id: 'users',
          label: 'user',
          url: '/ev-admin/user-management/user',
          allow: permissionCheck('user_management'),
        },
        {
          id: 'roles',
          label: 'role',
          url: '/ev-admin/user-management/role',
          allow: permissionCheck('user_management'),
        },
      ],
    },
    {
      id: 'tools',
      label: 'tools',
      svgIcon: <QRCodeIcon />,
      activeSvgIcon: <QRCodeActiveIcon />,
      allow: true,
      children: [
        {
          id: 'qrCode',
          label: 'qrCodeGenerator',
          url: '/ev-admin/device-qr-management',
          allow: permissionCheck('qr_code_management'),
        },
        {
          id: 'campaigns',
          label: 'campaign',
          url: '/ev-admin/campaign-management',
          allow: permissionCheck('campaign_management'),
        },
      ],
    },
  ]);

  /* Accordion Logic: Auto-collapse other root items when one opens */
  /**
   * Update the menu open state
   * @param items - Menu items
   * @param id - Target ID
   * @param depth - Recursion depth
   * @returns Updated menu items
   */
  function updateMenuOpenState(items: MenuItem[], id: string, depth: number = 0): MenuItem[] {
    // Check if the target ID is a direct child of this list (to handle root exclusive toggle)
    const isTargetRoot = depth === 0 && items.some((item) => item.id === id);

    if (isTargetRoot) {
      return items.map((item) => ({
        ...item,
        isOpen: item.id === id ? !item.isOpen : false, // Close others if root
      }));
    }

    return items.map((item) => {
      if (item.id === id) {
        return { ...item, isOpen: !item.isOpen };
      }
      if (item.children) {
        return { ...item, children: updateMenuOpenState(item.children, id, depth + 1) };
      }
      return item;
    });
  }

  /**
   * Toggle submenu
   * @param id - Menu item ID
   */
  function toggleSubMenu(id: string) {
    // Simply toggle the menu item state.
    // Floating popups will handle the display if sidebar is collapsed.
    setMenuItems((previous) => updateMenuOpenState(previous, id));
  }

  // Helper to strictly close all menus (used when navigating from floating popup)
  /**
   * Close all menus
   * @param items - Menu items
   * @returns Closed menu items
   */
  function closeAllMenus(items: MenuItem[]): MenuItem[] {
    return items.map((item) => ({
      ...item,
      isOpen: false,
      children: item.children ? closeAllMenus(item.children) : undefined,
    }));
  }

  /**
   * Handle navigation
   * @param url - Target URL
   */
  function handleNavigate(url?: string) {
    if (url) {
      if (!expandSidebar) {
        setMenuItems((previous) => closeAllMenus(previous));
      }
      navigate(url);
    }
  }

  /**
   * Logout function
   */
  const handleLogout = () => {
    authApi
      .logout({
        accessToken: getCookie('ev-access-token'),
      })
      .then(() => {
        removeCookie('ev-access-token');
        removeCookie('ev-refresh-token');
        navigate('/login');
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message || 'Logout failed',
            code: error?.response?.data?.code,
            status: true,
            type: 'error',
          }),
        );
      });
  };

  /**
   * Check if route is active
   * @param itemUrl - URL to check
   * @param activeMatch - Custom match string
   * @returns True if active
   */
  const isRouteActive = React.useCallback(
    (itemUrl?: string, activeMatch?: string) => {
      if (activeMatch) {
        return location.pathname.includes(activeMatch);
      }
      if (!itemUrl) return false;
      return location.pathname.includes(itemUrl) && itemUrl !== '/ev-admin';
    },
    [location.pathname],
  );

  // Helper to check if any child is active
  /**
   * Check if parent is active
   * @param item - Menu item
   * @returns True if active
   */
  const isParentActive = React.useCallback(
    (item: MenuItem): boolean => {
      if (item.url && isRouteActive(item.url, item.activeMatch)) return true;
      if (item.children) {
        return item.children.some((child) => isParentActive(child));
      }
      return false;
    },
    [isRouteActive],
  );

  // Render a single menu item (recursive)
  /**
   * Render menu item
   * @param item - Menu item
   * @param depth - Depth
   * @returns JSX element
   */
  function renderMenuItem(item: MenuItem, depth: number = 0) {
    const { allow, children, url, isOpen, id, label, svgIcon, activeSvgIcon, activeMatch } = item;
    if (!allow) return null;

    const hasChildren = children && children.length > 0;
    const isActive = isRouteActive(url, activeMatch);
    const isParent = hasChildren && isParentActive(item);

    return (
      <div key={id} className={`menu-item-container depth-${depth}`}>
        <div
          className={`sidebar-menu-item ${isActive ? 'active' : ''} ${isParent || (hasChildren && isOpen) ? 'parent-active' : ''}`}
          onClick={() => {
            if (hasChildren) {
              toggleSubMenu(id);
            } else {
              handleNavigate(url);
            }
          }}
          title={!expandSidebar && depth === 0 ? t(label) : ''}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              if (hasChildren) {
                toggleSubMenu(id);
              } else {
                handleNavigate(url);
              }
            }
          }}>
          {depth === 0 && (
            <div className={`icon-container ${isActive || isParent ? 'active' : ''}`}>
              <span className="menu-icon">{isActive || isParent ? activeSvgIcon : svgIcon}</span>
            </div>
          )}

          {/* Show label if Expanded OR if we are in a floating popup (depth > 0) */}
          {(expandSidebar || depth > 0) && (
            <div className="label-container">
              <span className="menu-label">{label.includes(' ') ? label : t(label)}</span>
              {hasChildren && (
                <span className="chevron">
                  {isOpen ? (
                    <ChevronDown />
                  ) : (
                    <span style={{ transform: i18n.language === 'ar' ? 'rotate(180deg)' : 'none' }}>
                      <ChevronRight />
                    </span>
                  )}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Render children if open. 
            Use 'submenu-container' for inline tree (expanded sidebar).
            Use 'floating-menu-container' for popup menu (collapsed sidebar or nested popup). 
        */}
        {hasChildren && isOpen && (
          <div className={expandSidebar ? 'submenu-container' : 'floating-menu-container'}>
            {!expandSidebar && (
              <div className="floating-header">
                {svgIcon && <span className="header-icon">{svgIcon}</span>}
                {t(label)}
              </div>
            )}
            {children!.map((child) => renderMenuItem(child, depth + 1))}
          </div>
        )}
      </div>
    );
  }

  useEffect(() => {
    /**
     * Open active menus
     * @param items - Menu items
     * @returns Updated menu items
     */
    const openActiveMenus = (items: MenuItem[]): MenuItem[] => {
      return items.map((item) => {
        const active = isParentActive(item);
        const newItem = { ...item };
        // Only auto-expand if sidebar is expanded.
        // If collapsed (floating mode), do NOT auto-open on route change.
        if (expandSidebar && active && item.children) {
          newItem.isOpen = true;
          newItem.children = openActiveMenus(item.children);
        } else if (item.children) {
          newItem.children = openActiveMenus(item.children);
        }
        return newItem;
      });
    };
    setMenuItems((previous) => openActiveMenus(previous));
  }, [location.pathname, expandSidebar, isParentActive]);

  const [appVersion, setAppVersion] = useState<string>('');

  /**
   * handleVersion fetches the current app version from version.txt
   */
  const handleVersion = async () => {
    try {
      // ✅ Automatically pick current domain
      const baseUrl = window.location.origin;
      const url = `${baseUrl}/version.txt`;

      const response = await fetch(url, {
        method: 'GET',
        cache: 'no-cache',
      });

      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }

      const text = await response.text();

      // ❗ Avoid HTML error response
      if (!text || text.includes('<!DOCTYPE html>')) {
        setAppVersion('');
        return;
      }

      setAppVersion(text.trim());
    } catch (error) {
      console.error('Version fetch failed:', error);
      setAppVersion('');
    }
  };

  useEffect(() => {
    handleVersion();
  }, []);
  return (
    <div className="sidebar-wrap">
      <div
        className={`sidebar-container ${expandSidebar ? 'expand' : 'close'} ${i18n.language === 'ar' ? 'rtl' : ''}`}
        ref={sidebarReference}>
        <div className="sidebar-logo">
          <img
            src={brandImage.logo}
            alt="brand-logo"
            className={cachedLocal?.clientName === 'saqr-tech' ? 'saqr-tech' : ''}
          />
        </div>
        <div
          className="toggle-button"
          onClick={() => {
            if (expandSidebar) {
              setMenuItems((previous) => closeAllMenus(previous));
            }
            setExpandSidebar(!expandSidebar);
          }}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              if (expandSidebar) {
                setMenuItems((previous) => closeAllMenus(previous));
              }
              setExpandSidebar(!expandSidebar);
            }
          }}>
          {expandSidebar ? (
            <svg
              width="6"
              height="8"
              viewBox="0 0 6 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ transform: i18n.language === 'ar' ? 'none' : 'rotate(180deg)' }}>
              <path
                d="M1 9L5 5L1 1"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : (
            <svg
              width="6"
              height="8"
              viewBox="0 0 6 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ transform: i18n.language === 'ar' ? 'rotate(180deg)' : 'none' }}>
              <path
                d="M1 9L5 5L1 1"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </div>

        <div className="sidebar-scroll-area">{menuItems.map((item) => renderMenuItem(item))}</div>
        <div className="sidebar-language-selector">
          <LanguageSelector isCollapsed={!expandSidebar} />
        </div>
        <div
          className="sidebar-logout"
          role="button"
          tabIndex={0}
          onClick={handleLogout}
          onKeyDown={(event) => event.key === 'Enter' && handleLogout()}>
          <span className="logout-icon">
            <LogoutIcon />
          </span>
          {expandSidebar && <span className="logout-text">{t('logout')}</span>}
        </div>
        <div className="app-version">
          {branding.meta.footerText && <PowerByEnergic />}
          <span>{appVersion}</span>
        </div>
      </div>
    </div>
  );
}

export default Sidebar;
